import { api } from '@/lib/axios';

export type UserMessageTab = 'Inbox' | 'Sent' | 'Closed';
export type UserMessageType = 'Alert' | 'Warning' | 'New' | 'Message' | 'User Message' | 'Get Help';

export interface UserMessageListItem {
  messageKey: number;
  messageGuid: string;
  subject: string;
  senderName: string;
  mrn: string | null;
  messageType: string;
  sentDate: string;
  openFlag: string;
  readFlag: boolean;
}

export interface UserMessagePage {
  items: UserMessageListItem[];
  rowLimit: number;
  limitReached: boolean;
}

export interface UserMessageReply {
  messageKey: number;
  fromName: string;
  sentDate: string;
  body: string;
}

export interface UserMessageDetail {
  messageKey: number;
  messageGuid: string;
  subject: string;
  fromName: string;
  fromGroup: string | null;
  toGroup: string;
  body: string;
  sentDate: string;
  replies: UserMessageReply[];
  categoryGroup: string | null;
  notes: string | null;
}

export interface CategoryGroupOption {
  code: string;
  description: string;
}

export interface UpdateMessageCategoryRequest {
  categoryGroup: string | null;
  notes: string | null;
}

export interface PatientSearchResult {
  userKey: number;
  lastName: string;
  firstName: string;
  mrn: string | null;
  username: string;
}

export interface UserMessageSearchParams {
  tab: UserMessageTab;
  search?: string;
  fromDate?: string | null;
  toDate?: string | null;
}

export interface ReplyRequest {
  subject: string;
  message: string;
  secureEmail: boolean;
}

export interface ComposeRequest {
  toUserKeys: number[];
  sendToAll: boolean;
  fromMessageGroup: string;
  type: string;
  subject: string;
  message: string;
}

export interface ForwardData {
  subject: string;
  emailBody: string;
}

export interface OpenCountResult {
  count: number;
}

export interface MessageGroupOption {
  code: string;
  description: string;
}

/** Shared cache prefix for admin user-message queries. */
export const USER_MESSAGES_QUERY_ROOT = 'user-messages';
export const USER_MESSAGES_UNREAD_QUERY_KEY = [USER_MESSAGES_QUERY_ROOT, 'unread-count'] as const;
/** How often the nav badge (and inbox list) refresh while the admin portal is open. */
export const USER_MESSAGES_POLL_MS = 120_000;

export const userMessageService = {
  getOpenCount: () =>
    api.get<OpenCountResult>('/admin/UserMessages/OpenCount').then((r) => r.data),

  getUnreadCount: () =>
    api.get<OpenCountResult>('/admin/UserMessages/UnreadCount').then((r) => r.data),

  search: (params: UserMessageSearchParams) =>
    api
      .get<UserMessagePage>('/admin/UserMessages/Search', { params })
      .then((r) => r.data),

  get: (guid: string, markAsRead = true) =>
    api
      .get<UserMessageDetail>(`/admin/UserMessages/${guid}`, { params: { markAsRead } })
      .then((r) => r.data),

  toggleOpen: (guid: string) =>
    api.put<{ messageGuid: string; openFlag: string; openCount: number }>(
      `/admin/UserMessages/${guid}/toggle`
    ).then((r) => r.data),

  close: (guid: string) =>
    api.put<{ messageGuid: string; openFlag: string; openCount: number }>(
      `/admin/UserMessages/${guid}/close`
    ).then((r) => r.data),

  reopen: (guid: string) =>
    api.put<{ messageGuid: string; openFlag: string; openCount: number }>(
      `/admin/UserMessages/${guid}/reopen`
    ).then((r) => r.data),

  reply: (guid: string, body: ReplyRequest) =>
    api.post(`/admin/UserMessages/${guid}/reply`, body).then((r) => r.data),

  compose: (body: ComposeRequest) =>
    api.post('/admin/UserMessages', body).then((r) => r.data),

  searchPatients: (name: string) =>
    api
      .get<PatientSearchResult[]>('/admin/UserMessages/SearchPatients', { params: { name } })
      .then((r) => r.data),

  getForwardData: (guid: string) =>
    api.get<ForwardData>(`/admin/UserMessages/${guid}/ForwardData`).then((r) => r.data),

  getMessageGroups: () =>
    api.get<MessageGroupOption[]>('/admin/UserMessages/MessageGroups').then((r) => r.data),

  getCategoryGroups: () =>
    api.get<CategoryGroupOption[]>('/admin/UserMessages/CategoryGroups').then((r) => r.data),

  updateCategory: (guid: string, body: UpdateMessageCategoryRequest) =>
    api.put(`/admin/UserMessages/${guid}/category`, body).then((r) => r.data),
};
